/**
 * Thin wrapper over the Telegram WebApp SDK. Loaded from
 * https://telegram.org/js/telegram-web-app.js in index.html.
 *
 * If the script failed to load or the page is open outside TMA, all helpers
 * return safe nulls so callers can fall back to dev-mode auth.
 */

interface TgUser {
  id: number;
  is_bot?: boolean;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TgUser;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportStableHeight: number;
  isVerticalSwipesEnabled?: boolean;
  ready: () => void;
  expand: () => void;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

function getApp(): TgWebApp | null {
  return typeof window !== "undefined" ? window.Telegram?.WebApp ?? null : null;
}

/** Raw initData string for the "tma <initData>" auth header. Empty if not in TMA. */
export function getInitData(): string {
  return getApp()?.initData ?? "";
}

/** Parsed initData (user info, hash). All fields optional — outside TMA returns {}. */
export function getInitDataUnsafe(): TgWebApp["initDataUnsafe"] {
  return getApp()?.initDataUnsafe ?? {};
}

/** Heuristic: are we running inside Telegram? */
export function isInTelegram(): boolean {
  return getInitData().length > 0;
}

/** Tell TG that the WebApp finished rendering and can be shown. Safe-no-op outside TMA. */
export function tgReady(): void {
  getApp()?.ready();
}

/**
 * Lock the viewport for drag-heavy UI: expand to full height and DISABLE Telegram's
 * vertical swipe-to-close. Without this, dragging the crop frame (or any vertical
 * gesture) down is read by Telegram as "swipe to close" and kicks the operator out
 * of the Mini App mid-edit. Feature-detected → safe no-op outside TMA or on Telegram
 * clients older than Bot API 7.7. The X button still closes the app normally.
 */
export function tgLockViewport(): void {
  const app = getApp();
  if (!app) return;
  try { app.expand?.(); } catch { /* noop */ }
  try { app.disableVerticalSwipes?.(); } catch { /* noop */ }
}

/** Light haptic tap. Used for confirmations, slot picks, etc. */
/** Открыть переписку по значению поля telegram брони — «@ник» или числовой ID (25.08).
 *  В TMA t.me-ссылки идут через openTelegramLink (без выхода из мини-аппа);
 *  tg://user?id= для ID — window.open с фолбэком в location. */
export function openTgChat(raw: string): void {
  const v = (raw || "").trim().replace(/^@/, "");
  if (!v) return;
  if (/^\d+$/.test(v)) {
    /* числовой ID: t.me-ссылки не существует, только tg://-схемы. window.open в
       TMA возвращает объект даже не открыв ничего — поэтому location.href; iOS и
       Android понимают разные схемы — каскад: если чат открылся, вебвью теряет
       видимость и второй переход не выполняется (26.08, владелец). */
    window.location.href = `tg://user?id=${v}`;
    window.setTimeout(() => { if (!document.hidden) window.location.href = `tg://openmessage?user_id=${v}`; }, 500);
    return;
  }
  const url = `https://t.me/${v}`;
  const wa = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
  if (wa?.openTelegramLink) { wa.openTelegramLink(url); return; }
  if (!window.open(url, "_blank")) window.location.href = url;
}

/** Чат по НОМЕРУ телефона — t.me/+7…: единственный надёжный путь к клиенту без
 *  ника (по голому ID Telegram открывает чат только «знакомым» профилям). */
export function openTgChatByPhone(rawPhone: string): void {
  let d = (rawPhone || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("8")) d = `7${d.slice(1)}`;
  if (d.length < 10) return;
  const url = `https://t.me/+${d}`;
  const wa = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
  if (wa?.openTelegramLink) { wa.openTelegramLink(url); return; }
  if (!window.open(url, "_blank")) window.location.href = url;
}

/** 03.10 (кадры): «Отправить» ссылку-приглашение — окно Telegram «Поделиться» (выбор чата).
 *  В TMA — openTelegramLink (без выхода из мини-аппа), вне — новая вкладка. */
export function openTgShare(url: string, text: string): void {
  const link = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  const wa = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
  if (wa?.openTelegramLink) { wa.openTelegramLink(link); return; }
  if (!window.open(link, "_blank")) window.location.href = link;
}

export function tgHaptic(kind: "tap" | "success" | "warn" | "error" = "tap"): void {
  const hf = getApp()?.HapticFeedback;
  if (!hf) return;
  if (kind === "tap") hf.impactOccurred("light");
  else if (kind === "success") hf.notificationOccurred("success");
  else if (kind === "warn") hf.notificationOccurred("warning");
  else hf.notificationOccurred("error");
}
